/**
 * SEO component that queries for data with
 *  Gatsby's useStaticQuery React hook
 *
 * See: https://www.gatsbyjs.org/docs/use-static-query/
 */

import React from "react"
import PropTypes from "prop-types"
import {Helmet} from "react-helmet"
import { useStaticQuery, graphql } from "gatsby"

import sharingImage from '../../resources/img/social/sharing.png';

function SEO({ description, lang, meta, fullTitle, title, image, path, noIndex, isHomePage }) {
  const { site } = useStaticQuery(
    graphql`
      query {
        site {
          siteMetadata {
            title
            description
            author
            siteUrl
          }
        }
      }
    `
  );

  const websiteUrl = site.siteMetadata.siteUrl || process.env.WEBSITE_URL;

  const metaTitle = title || site.siteMetadata.title;
  const metaDescription = description || site.siteMetadata.description;
  const socialSharingImage = image || websiteUrl + sharingImage;

  // Canonical URL: strip trailing slash so it matches the served URLs
  // (gatsby-plugin-remove-trailing-slashes). Omitted when no path is known (e.g. 404).
  let canonicalUrl = null;
  if (typeof path === 'string') {
    const url = websiteUrl + path;
    canonicalUrl = url.length > websiteUrl.length && url.substr(-1) === '/'
      ? url.substr(0, url.length - 1)
      : url;
  }

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "Organization",
      "@id": `${websiteUrl}/#organization`,
      name: "Theme Deploy",
      url: websiteUrl,
      logo: socialSharingImage,
    },
    {
      "@context": "https://schema.org",
      "@type": "WebSite",
      "@id": `${websiteUrl}/#website`,
      name: site.siteMetadata.title,
      description: site.siteMetadata.description,
      url: websiteUrl,
      publisher: { "@id": `${websiteUrl}/#organization` },
    },
  ];

  if (isHomePage) {
    structuredData.push({
      "@context": "https://schema.org",
      "@type": "SoftwareApplication",
      name: "Theme Deploy",
      description: metaDescription,
      url: websiteUrl,
      applicationCategory: "DeveloperApplication",
      operatingSystem: "Web",
      offers: {
        "@type": "Offer",
        price: "0",
        priceCurrency: "USD",
        description: "Free to try",
      },
    });
  }

  return (
    <Helmet
      htmlAttributes={{
        lang,
      }}
      title={metaTitle}
      titleTemplate={fullTitle ? `%s` : `%s | ${site.siteMetadata.title}`}
      link={canonicalUrl ? [{ rel: `canonical`, href: canonicalUrl }] : []}
      meta={[
        {
          name: `description`,
          content: metaDescription,
        },
        ...(noIndex ? [{ name: `robots`, content: `noindex` }] : []),
        ...(canonicalUrl ? [{ property: `og:url`, content: canonicalUrl }] : []),
        {
          property: `og:title`,
          content: metaTitle,
        },
        {
          property: `og:description`,
          content: metaDescription,
        },
        {
          property: `og:type`,
          content: `website`,
        },
        {
          property: `og:site_name`,
          content: `Theme Deploy`,
        },
        {
          property: `og:locale`,
          content: `en_US`,
        },
        {
          property: `og:image`,
          content: socialSharingImage,
        },
        {
          property: `fb:app_id`,
          content: `2243518289231235`,
        },
        {
          name: `twitter:card`,
          content: `summary`,
        },
        {
          name: `twitter:creator`,
          content: site.siteMetadata.author,
        },
        {
          name: `twitter:title`,
          content: metaTitle,
        },
        {
          name: `twitter:description`,
          content: metaDescription,
        },
        {
          name: `twitter:image`,
          content: socialSharingImage,
        },
      ]
        .concat(meta)}
    >
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  )
}

SEO.defaultProps = {
  lang: `en`,
  meta: [],
  noIndex: false,
  isHomePage: false,
};

SEO.propTypes = {
  description: PropTypes.string,
  lang: PropTypes.string,
  meta: PropTypes.array,
  title: PropTypes.string,
  path: PropTypes.string,
  noIndex: PropTypes.bool,
  isHomePage: PropTypes.bool,
};

export default SEO
